/**
 * Shared "contextual guidance" types for Patient360 tabs.
 *
 * A GuidanceItem is a normalized, tab-agnostic representation of "what does
 * the user need to know/do right now" — every tab derives its own items from
 * data/permissions it already has, then renders them through the single
 * shared <TabGuidancePanel />.
 */

export type GuidanceSeverity = 'blocking' | 'warning' | 'info';

export const GUIDANCE_SEVERITY_ORDER: Record<GuidanceSeverity, number> = {
  blocking: 0,
  warning: 1,
  info: 2,
};

/**
 * An action is either performable now (`onClick`) or blocked for the current
 * user/workflow state (`disabledReason`) — never both. When blocked, the
 * panel renders the reason instead of a dead button (no hardcoded role
 * names — the reason string comes from the same permission/workflow check
 * that gates the real action elsewhere on that tab).
 */
export type GuidanceAction =
  | { label: string; onClick: () => void; disabledReason?: undefined }
  | { label: string; onClick?: undefined; disabledReason: string };

export interface GuidanceItem {
  id: string;
  severity: GuidanceSeverity;
  issue: string;
  detail?: string;
  primaryAction?: GuidanceAction;
  secondaryAction?: GuidanceAction;
}

export function sortGuidanceItems(items: GuidanceItem[]): GuidanceItem[] {
  return [...items].sort((a, b) => GUIDANCE_SEVERITY_ORDER[a.severity] - GUIDANCE_SEVERITY_ORDER[b.severity]);
}
